/**
 * calibrate · «откалибруй баг: Severity × Priority» two-axis drill (S04 TD-07).
 *
 * Topic-fit practice for Severity vs Priority: for each bug case the student sets
 * BOTH axes — Severity (technical) and Priority (business) — and gets a per-axis
 * verdict plus a «типичная / контр-интуитивная» tag and explanation. A case counts
 * only when both axes are right. Built around the counter-intuitive traps (typo on
 * home page = Trivial × P0; crash in a dead feature = Critical × P3) the quickCheck
 * and AC-04/06 hinge on. A different shape from every prior S04 mechanic.
 *
 * Markup contract (authored content · no-JS degradable — without this script each
 * `.case-fb-src` shows inline so the case + разбор read as static text):
 *   <div class="cal" data-sev="Critical|Major|Minor|Trivial" data-pri="P0|P1|P2|P3">
 *     <div class="cal-head"><span class="ttl">Откалибруй баг</span>
 *       <span class="cal-cnt">верно <b class="cal-num">0</b>/5</span></div>
 *     <div class="cal-intro">…</div>
 *     <div class="cal-list">
 *       <div class="case" data-sevi="0" data-prii="0" data-kind="typ">
 *         <div class="case-txt">…сценарий…</div>
 *         <div class="case-fb-src">…разбор (копируется в вердикт)…</div>
 *       </div>
 *       … cases · data-sevi/data-prii = индексы верных Severity/Priority · data-kind typ|counter …
 *     </div>
 *     <div class="cal-fin">…payoff (revealed when all cases done)…</div>
 *   </div>
 */

const KIND_LABEL: Record<string, string> = {
  typ: 'типичная комбинация',
  counter: 'контр-интуитивная',
};

export function initCalibrate(root: HTMLElement): () => void {
  if (typeof document === 'undefined') return () => {};
  const cleanups: Array<() => void> = [];

  const cals = Array.from(root.querySelectorAll<HTMLElement>('.cal'));
  for (const cal of cals) {
    if (cal.dataset.calInit === '1') continue;
    cal.dataset.calInit = '1';
    const original = cal.innerHTML; // restore on cleanup — StrictMode (dev) re-init safety
    cal.classList.add('cal-ready');

    const sevLabels = (cal.dataset.sev ?? 'Critical|Major|Minor|Trivial').split('|');
    const priLabels = (cal.dataset.pri ?? 'P0|P1|P2|P3').split('|');
    // Авторские названия осей (S03 RBT: «Вероятность» × «Импакт»). Фолбэк — S04 Severity/Priority.
    const axisSName = cal.dataset.axisS ?? 'Severity · техническое влияние';
    const axisPName = cal.dataset.axisP ?? 'Priority · бизнес-срочность';
    const numEl = cal.querySelector<HTMLElement>('.cal-num');
    const fin = cal.querySelector<HTMLElement>('.cal-fin');
    // `.case.example` — авторский разобранный кейс (S03): статичен, в дрилл не входит.
    const cases = Array.from(cal.querySelectorAll<HTMLElement>('.case:not(.example)'));
    if (cases.length === 0) continue;

    let correct = 0;

    // Карусель (Genie 10 JUN: «практика растянута вниз — собрать компактно»):
    // один кейс на экране, точки-прогресс в шапке, «Дальше» после проверки.
    cal.classList.add('cal-deck');
    let idx = 0;
    const results: Array<boolean | null> = cases.map(() => null);
    const dots = document.createElement('span');
    dots.className = 'cal-dots';
    cases.forEach(() => dots.appendChild(document.createElement('i')));
    cal.querySelector('.cal-head')?.insertBefore(dots, cal.querySelector('.cal-cnt'));
    const paintDots = () => {
      Array.from(dots.children).forEach((d, k) => {
        d.className =
          (k === idx ? 'cur' : '') +
          (results[k] === true ? ' ok' : results[k] === false ? ' no' : '');
      });
    };
    const showCase = () => {
      cases.forEach((c, k) => c.classList.toggle('cur', k === idx));
      paintDots();
    };
    const foot = document.createElement('div');
    foot.className = 'cal-foot';
    const nextB = document.createElement('button');
    nextB.type = 'button';
    nextB.className = 'cal-next';
    nextB.textContent = 'Дальше';
    foot.appendChild(nextB);
    (cal.querySelector('.cal-list') ?? cal).insertAdjacentElement('afterend', foot);
    const onNext = () => {
      if (idx < cases.length - 1) {
        idx++;
        nextB.classList.remove('show');
        showCase();
      } else {
        cal.classList.add('cal-done');
        nextB.classList.remove('show');
        fin?.classList.add('on');
        paintDots();
      }
    };
    nextB.addEventListener('click', onNext);
    cleanups.push(() => nextB.removeEventListener('click', onNext));
    showCase();

    const axisHtml = (axis: string, labels: string[]) =>
      `<div class="axis"><div class="axis-lab">${axis === 's' ? axisSName : axisPName}</div>` +
      `<div class="opts" data-axis="${axis}">` +
      labels.map((x, k) => `<button type="button" class="opt" data-k="${k}">${x}</button>`).join('') +
      `</div></div>`;

    cases.forEach((cs, i) => {
      const sevi = Number(cs.dataset.sevi ?? 0);
      const prii = Number(cs.dataset.prii ?? 0);
      const kind = cs.dataset.kind ?? 'typ';
      const fbSrc = cs.querySelector<HTMLElement>('.case-fb-src')?.innerHTML ?? '';

      const num = document.createElement('div');
      num.className = 'case-num';
      num.textContent = cal.querySelector('.case.example')
        ? `фича ${i + 1} из ${cases.length} · теперь ты`
        : `кейс ${i + 1} из ${cases.length}`;
      cs.insertBefore(num, cs.firstChild);

      const block = document.createElement('div');
      block.innerHTML =
        axisHtml('s', sevLabels) +
        axisHtml('p', priLabels) +
        `<div class="case-act"><button type="button" class="case-check" disabled>Проверить</button></div>` +
        `<div class="case-fb" aria-live="polite"></div>`;
      while (block.firstChild) cs.appendChild(block.firstChild);

      const sOpts = Array.from(cs.querySelectorAll<HTMLButtonElement>('[data-axis="s"] .opt'));
      const pOpts = Array.from(cs.querySelectorAll<HTMLButtonElement>('[data-axis="p"] .opt'));
      const check = cs.querySelector<HTMLButtonElement>('.case-check');
      const fbEl = cs.querySelector<HTMLElement>('.case-fb');
      if (!check || !fbEl) return;

      const state = { s: null as number | null, p: null as number | null, done: false };

      const wire = (opts: HTMLButtonElement[], axis: 's' | 'p') => {
        for (const o of opts) {
          const onPick = () => {
            if (state.done) return;
            opts.forEach((x) => x.classList.remove('pick'));
            o.classList.add('pick');
            state[axis] = Number(o.dataset.k);
            check.disabled = state.s === null || state.p === null;
          };
          o.addEventListener('click', onPick);
          cleanups.push(() => o.removeEventListener('click', onPick));
        }
      };
      wire(sOpts, 's');
      wire(pOpts, 'p');

      const onCheck = () => {
        if (state.done) return;
        state.done = true;
        const sOk = state.s === sevi;
        const pOk = state.p === prii;
        const allOk = sOk && pOk;
        [
          { opts: sOpts, want: sevi, got: state.s },
          { opts: pOpts, want: prii, got: state.p },
        ].forEach(({ opts, want, got }) => {
          opts.forEach((o, k) => {
            o.classList.add('lock');
            if (k === got) o.classList.add(k === want ? 'right' : 'wrong');
            if (k === want && got !== want) o.classList.add('mustbe');
          });
        });
        check.style.display = 'none';
        if (allOk) correct += 1;
        if (numEl) numEl.textContent = String(correct);
        fbEl.className = `case-fb on ${allOk ? 'ok' : 'no'}`;
        fbEl.innerHTML =
          `<div class="case-verdict">${allOk ? 'Верно: ' : 'Не совсем — верно '}${sevLabels[sevi]} × ${priLabels[prii]}</div>` +
          `<span class="case-tag ${kind === 'typ' ? 'typ' : 'counter'}">${KIND_LABEL[kind] ?? kind}</span>` +
          `<div>${fbSrc}</div>`;
        // карусель: результат в точку-прогресс + показать «Дальше» / «Итог»
        results[i] = allOk;
        paintDots();
        nextB.textContent = i === cases.length - 1 ? 'Итог' : 'Дальше';
        nextB.classList.add('show');
      };
      check.addEventListener('click', onCheck);
      cleanups.push(() => check.removeEventListener('click', onCheck));
    });

    cleanups.push(() => {
      cal.innerHTML = original;
      delete cal.dataset.calInit;
    });
  }

  return () => cleanups.forEach((fn) => fn());
}
